import React, { addReducer, useState, useDispatch, getGlobal, setGlobal } from "reactn"
import { graphql } from "gatsby"
import { createStorefrontApiClient } from "@shopify/storefront-api-client"
import { Global } from "@emotion/react"
import { ChakraProvider, extendTheme } from "@chakra-ui/react"
import myTheme from "../styles/theme"
import fonts from "../styles/fonts"
import { LeftDrawer } from "../components/navigation/drawer"
import { Header } from "../components/navigation/header"
import { Helmet } from "react-helmet"
import { setUser } from "../components/auth/auth"
import Logging from "../components/utils/logging"
import ErrorBoundary from "../components/utils/ErrorBoundary"
import { createContext, useContext } from "react"
import { OnlineStatusProvider } from "../components/utils/onlineStatus"

// Import refactored modules
import { STOREFRONT_CONFIG } from "./constants"
import { cartEmpty, defaultValues } from "./initialState"
import { initializeStorage, getStoredCartData, saveState } from "./utils/storage"
import { updateCartTotals, calculateTotals } from "./utils/cartCalculations"
import { cartOpened, checkoutOpened, paymentOpened, itemAdded, itemRemoved, discountApplied, paymentFailed } from "./analytics"
import { clearShipping, fetchCartState, warmUpCartLambda } from "./network"
import { API } from 'aws-amplify'

// Import reducers
import { setPromoCode, clearPromoCode, setDiscount, clearDiscount } from "./reducers/promoReducers"
import { setExpressShipping, setShipping, setPackagingOption, setAddress, differentBilling } from "./reducers/shippingReducers"
import { paymentComplete } from "./reducers/paymentReducers"

const theme = extendTheme(myTheme)
let ls
let cartData

/**
 * Create Shopify Storefront API client
 * @returns {Object|null} Storefront API client or null if server-side
 */
const getClient = () => {
  if (typeof window === "undefined") return null
  return createStorefrontApiClient(STOREFRONT_CONFIG)
}

const client = getClient()

// Export getClient function for use in other components
export { getClient }

/**
 * Initialize and get store state
 * @returns {Object} Initial store state
 */
function getStore() {
  let initialState = {}

  // Initialize storage
  ls = initializeStorage()
  cartData = getStoredCartData()

  // Check if we're on the payment page - don't restore cart data on payment page
  const isOnPaymentPage = typeof window !== 'undefined' && window.location.pathname.includes('/payment')

  if (cartData && !isOnPaymentPage) {
    const cartJSON = JSON.parse(cartData)
    console.log("CART JSON", cartJSON)
    initialState = {
      ...defaultValues,
      ...cartEmpty,
      client,
      // Restore saved state
      scarfOrder: cartJSON.scarfOrder || {},
      links: cartJSON.links || [],
      scarves: cartJSON.scarves || {},
      scarfID: cartJSON.scarfID || "",
      sid: cartJSON.sid,
      countryChange: cartJSON.countryChange || false,
      cartAvailable: true,
      cartInitialised: false,
      promoCode: cartJSON.promoCode?.promo_code ? cartJSON.promoCode : cartEmpty.promoCode,
      ss: cartJSON.ss || {},
      differentBillingAddress: cartJSON.differentBillingAddress || false,
      cartState: cartJSON.cartState || cartEmpty.cartState,
      emailAddress: cartJSON.emailAddress || "",
      contactNumber: cartJSON.contactNumber || "",
      shipping_address: cartJSON.shipping_address || cartEmpty.shipping_address,
      fullName: cartJSON.fullName || "",
      shippingID: cartJSON.shippingID || "",
      packagingID: cartJSON.packagingID || "",
      billing_address: cartJSON.billing_address || {},
      paymentAmount: cartJSON.paymentAmount,
      orderData: cartJSON.orderData || {},
      itemsInCart: cartJSON.itemsInCart || false,
      cartProducts: cartJSON.cartProducts || [],
      cartTotal: cartJSON.cartTotal || {
        productQuantity: 0,
        totalPrice: 0,
        currencyId: "GBP",
        currencyFormat: "£",
      },
      totals: cartJSON.totals || {
        shipping: 0,
        discount: 0,
        items: 0,
        total: 0,
      },
      // Explicitly restore cartOpen from storage
      cartOpen: false
      // cartJSON.cartOpen !== undefined ? cartJSON.cartOpen : false,
    }

  } else {
    initialState = { ...defaultValues, ...cartEmpty, client }
  }

  return initialState
}

export const GlobalContext = createContext(getStore())

/**
 * Check if we're on the payment page
 * @returns {boolean} True if on payment page
 */
const isPaymentPage = () => {
  if (typeof window === 'undefined') return false
  return window.location.pathname.includes('/payment')
}

/**
 * Empty the cart
 * @param {Object} currentStore - Current store state
 * @returns {Object} New store state
 */
export function emptyCart(currentStore) {
  if (!currentStore) {
    throw new Error('emptyCart requires a currentStore parameter')
  }

  // Preserve customer information so they don't have to fill it in again
  // Preserve address if it has any meaningful data (address1, city, country_code, etc.)
  // Don't require 'complete' flag - preserve any address data the user has entered
  const hasAddressData = currentStore.shipping_address && (
    currentStore.shipping_address.address1 ||
    currentStore.shipping_address.city ||
    currentStore.shipping_address.country_code ||
    currentStore.shipping_address.zip ||
    currentStore.shipping_address.name
  )

  const preservedCustomerInfo = {
    emailAddress: currentStore.emailAddress || "",
    fullName: currentStore.fullName || "",
    contactNumber: currentStore.contactNumber || "",
    shipping_address: hasAddressData
      ? { ...currentStore.shipping_address }
      : { ...cartEmpty.shipping_address },
  }

  return {
    ...currentStore,
    ...cartEmpty,
    // Preserve customer information for next checkout
    ...preservedCustomerInfo,
    // Preserve cartOpen state - don't close cart when emptying
    cartOpen: currentStore.cartOpen
  }
}

/**
 * Main site context provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Site context provider
 */
export const SiteContext = ({ children }) => {
  const [store, setStore] = useState(useContext(GlobalContext))

  // Create a ref to hold the current store state
  const storeRef = React.useRef(store)
  storeRef.current = store

  // Sync ReactN global state with Context store on mount and when store changes
  React.useEffect(() => {
    setGlobal(store)
  }, [store])

  /**
   * Update global state
   * @param {Object} objectToUpdate - Object to update state with
   */
  const updateGlobal = async (objectToUpdate) => {
    // Don't pass store as second parameter - let updateAndSave use the current store
    updateAndSave(objectToUpdate)
  }

  /**
   * Update and save state
   * @param {Object} newData - New data to merge
   * @param {Object} currentStore - Current store state
   */
  const updateAndSave = async (newData, currentStore) => {
    const storeToUse = currentStore || storeRef.current

    // Preserve cartOpen state if not explicitly provided in newData or if it's undefined
    const currentCartOpen = storeToUse.cartOpen
    const dataToSave = {
      ...newData,
      cartOpen: (newData.hasOwnProperty('cartOpen') && newData.cartOpen !== undefined) ? newData.cartOpen : currentCartOpen
    }

    const newStore = { ...storeToUse, ...dataToSave }

    // Update storeRef immediately so it's available for next operations
    storeRef.current = newStore

    // Sync ReactN global state with Context store
    setGlobal(newStore)

    setStore(newStore)
    saveState(newStore)

    // Return a promise that resolves after state is updated and saved
    // Use a microtask to ensure React has processed the state update
    return Promise.resolve()
  }

  // Effect hooks for cart management
  const prevCartOpenRef = React.useRef(false)
  const cartOpenedFiredRef = React.useRef(false)

  React.useEffect(() => {
    // When cart opens, fire analytics (don't fetch cart - wait until checkout button is pressed)
    if (store.cartOpen && !prevCartOpenRef.current) {
      // Fire analytics with available data
      cartOpened(store)
      cartOpenedFiredRef.current = true
    }
    prevCartOpenRef.current = store.cartOpen
  }, [store.cartOpen])

  // Fire cartOpened when cartProducts are loaded after cart was opened
  // Wait a bit for cart to load if it was just fetched
  React.useEffect(() => {
    if (store.cartOpen && !cartOpenedFiredRef.current) {
      // If cart was just opened and we're waiting for it to load, check if it's loaded now
      // cartProducts will be defined (even if empty) once the cart fetch completes
      if (store.cartProducts !== undefined) {
        // Small delay to ensure cart data is fully populated
        const timeoutId = setTimeout(() => {
          cartOpened(store)
          cartOpenedFiredRef.current = true
        }, 100)
        return () => clearTimeout(timeoutId)
      }
    }
    // Reset flag when cart closes
    if (!store.cartOpen) {
      cartOpenedFiredRef.current = false
    }
  }, [store.cartProducts, store.cartOpen, store.cartInitialised])

  // Effect hooks for checkout management
  const prevCheckoutOpenRef = React.useRef(false)
  const checkoutOpenedFiredRef = React.useRef(false)
  const fetchInitiatedRef = React.useRef(false)
  const promoCodeInitializedRef = React.useRef(false)
  const prevCheckoutOpenForPromoRef = React.useRef(false)

  React.useEffect(() => {
    // When checkout opens, only reset to shipping step if user hasn't progressed to payment yet
    // Only fetch once per checkoutOpen transition from false to true
    if (store.checkoutOpen && !prevCheckoutOpenRef.current && !fetchInitiatedRef.current) {
      fetchInitiatedRef.current = true

      // Only fetch cart if it's not already initialised (prevents over-fetching)
      if (!store.cartInitialised) {
        // Cart not initialised - reset to shipping step and fetch cart
        // CRITICAL: Reset preparePayment and showShipping to ensure we start at shipping step
        // Flow must be: Cart -> Confirm Shipping -> Pay (never skip shipping step)
        console.log('[ROOTSTATE DEBUG] Checkout opened - cart not initialised, resetting to shipping step and fetching');
        updateAndSave({
          preparePayment: false, // Ensure preparePayment is false - user hasn't clicked "CONTINUE TO PAYMENT" yet
          showPayment: false,     // Don't show payment yet
          showShipping: true,     // Always start with shipping step
          accordianItem: 0
        })
        fetchCartState(store, updateAndSave, true) // force: true to always fetch
      } else {
        // Cart already initialised - trust the existing state
        // Only update if state is clearly wrong (user on payment without preparePayment)
        // Don't update unnecessarily to prevent flash
        if (store.showPayment && !store.preparePayment) {
          // Invalid state - user on payment without clicking "CONTINUE TO PAYMENT"
          console.log('[ROOTSTATE DEBUG] Checkout opened - cart initialised but invalid state, resetting to shipping');
          updateAndSave({
            preparePayment: false,
            showPayment: false,
            showShipping: true,
            accordianItem: 0
          })
        } else {
          // State is fine - don't update to prevent flash
          console.log('[ROOTSTATE DEBUG] Checkout opened - cart initialised, using existing state (no update)');
        }
      }

      checkoutOpenedFiredRef.current = false
    }
    // Reset fetch flag when checkout closes
    if (!store.checkoutOpen && prevCheckoutOpenRef.current) {
      fetchInitiatedRef.current = false
    }
    prevCheckoutOpenRef.current = store.checkoutOpen
  }, [store.checkoutOpen])

  // Fire checkoutOpened when cartProducts are loaded after checkout was opened
  React.useEffect(() => {
    if (store.checkoutOpen && !checkoutOpenedFiredRef.current) {
      // If checkout was just opened and we're waiting for cart to load, check if it's loaded now
      if (store.cartProducts !== undefined) {
        // Small delay to ensure cart data is fully populated
        const timeoutId = setTimeout(() => {
          checkoutOpened(store)
          checkoutOpenedFiredRef.current = true
        }, 100)
        return () => clearTimeout(timeoutId)
      }
    }
    // Reset flag when checkout closes
    if (!store.checkoutOpen) {
      checkoutOpenedFiredRef.current = false
    }
  }, [store.cartProducts, store.checkoutOpen, store.cartInitialised])

  // Effect hooks for payment management
  const prevShowPaymentRef = React.useRef(false)
  const paymentOpenedFiredRef = React.useRef(false)

  React.useEffect(() => {
    // When payment page opens, fire analytics
    if (store.showPayment && !prevShowPaymentRef.current) {
      if (store.cartProducts !== undefined) {
        paymentOpened(store)
        paymentOpenedFiredRef.current = true
      } else {
        // Wait for cart to load
        paymentOpenedFiredRef.current = false
      }
    }
    prevShowPaymentRef.current = store.showPayment
  }, [store.showPayment])

  // Fire paymentOpened when cartProducts are loaded after payment was opened
  React.useEffect(() => {
    if (store.showPayment && !paymentOpenedFiredRef.current) {
      if (store.cartProducts !== undefined) {
        const timeoutId = setTimeout(() => {
          paymentOpened(store)
          paymentOpenedFiredRef.current = true
        }, 100)
        return () => clearTimeout(timeoutId)
      }
    }
    // Reset flag when payment closes
    if (!store.showPayment) {
      paymentOpenedFiredRef.current = false
    }
  }, [store.cartProducts, store.showPayment, store.cartInitialised])


  React.useEffect(() => {
    if (isPaymentPage()) return
    if (store.cartInitialised) {
      fetchCart(false)
    }
  }, [store.shippingID])

  // Track previous promo code to detect actual changes
  const prevPromoCodeRef = React.useRef(store.promoCode?.promo_code || "")

  React.useEffect(() => {
    if (isPaymentPage()) return

    // Skip on initial mount - only fetch when promo code actually changes
    if (!promoCodeInitializedRef.current) {
      promoCodeInitializedRef.current = true
      prevPromoCodeRef.current = store.promoCode?.promo_code || ""
      prevCheckoutOpenForPromoRef.current = store.checkoutOpen
      return
    }

    // Only fetch if checkout is open
    if (!store.checkoutOpen) {
      prevCheckoutOpenForPromoRef.current = store.checkoutOpen
      prevPromoCodeRef.current = store.promoCode?.promo_code || ""
      return
    }

    // Don't fetch if checkout just opened (checkoutOpen useEffect handles that)
    // Only fetch if promo code changed while checkout was already open
    if (!prevCheckoutOpenForPromoRef.current && store.checkoutOpen) {
      // Checkout just opened - skip this fetch, let checkoutOpen useEffect handle it
      prevCheckoutOpenForPromoRef.current = store.checkoutOpen
      prevPromoCodeRef.current = store.promoCode?.promo_code || ""
      return
    }

    // Only fetch if promo code actually changed (not just checkout opening)
    const promoCodeValue = store.promoCode?.promo_code || ""
    const prevPromoCodeValue = prevPromoCodeRef.current

    if (promoCodeValue === prevPromoCodeValue) {
      // Promo code didn't change - don't fetch
      prevCheckoutOpenForPromoRef.current = store.checkoutOpen
      return
    }

    // Promo code changed while checkout is open - fetch cart
    console.log('[PROMO DEBUG] Promo code changed in checkout, fetching cart with code:', promoCodeValue, 'cartInitialised:', store.cartInitialised)
    fetchCart(true)
    prevPromoCodeRef.current = promoCodeValue
    prevCheckoutOpenForPromoRef.current = store.checkoutOpen
  }, [store.promoCode?.promo_code, store.checkoutOpen])

  React.useEffect(() => {
    if (isPaymentPage()) return
    if (store.cartInitialised && store.cartUpdating) {
      console.log('[ROOTSTATE DEBUG] cartUpdating effect - skipping fetch (cart already initialised, cartUpdating=true)');
      // Don't fetch if cart is already initialised - cartUpdating is just a flag for UI state
      // fetchCart(false) // Commented out to prevent duplicate fetches
    }
  }, [store.cartUpdating])

  React.useEffect(() => {
    if (store.sid) {
      setUser(store.sid)
    } else {
      setUser("")
    }
  }, [store.sid])

  // Removed automatic cart fetch on IP change - now only fetches when cart is opened
  // React.useEffect(() => {
  //   if (isPaymentPage()) return
  //   if (store.ip && !store.cartInitialised) {
  //     fetchCart(true)
  //   }
  // }, [store.ip])

  // Cart warming effect - runs once when the app loads to warm up the lambda
  // Use a ref outside the component to ensure it only runs once globally, even if component remounts
  const hasWarmedUpRef = React.useRef(false)

  React.useEffect(() => {
    if (isPaymentPage()) return
    if (hasWarmedUpRef.current) {
      console.log('[ROOTSTATE DEBUG] Skipping warmup - already warmed up');
      return
    }

    hasWarmedUpRef.current = true
    // Make a lightweight call to warm up the cart lambda
    // This doesn't affect the user's cart state, just ensures the lambda is warm
    setTimeout(() => {
      try {
        console.log('[ROOTSTATE DEBUG] Warming up cart lambda');
        warmUpCartLambda()
      } catch (error) {
        // Silently handle errors - this is just for warming up
        console.log('[ROOTSTATE DEBUG] Warmup error:', error);
      }
    }, 2000) // 2 second delay to ensure everything is loaded
  }, []) // Empty dependency array means this runs once on mount

  // COMMENTED OUT: Prefetch carts when site loads - causing problems
  // Will do first fetch when going from Cart to checkout as before
  // We'll live with the loading/checking time
  // const hasPrefetchedRef = React.useRef(false)

  // React.useEffect(() => {
  //   if (isPaymentPage()) return
  //   if (hasPrefetchedRef.current) return
  //   if (!store.cartProducts?.length || !store.ss?.oid) return // Need cart items and session
  //   // Don't prefetch if checkout is already open - user is actively using it
  //   if (store.checkoutOpen) return

  //   hasPrefetchedRef.current = true

  //   const prefetchCarts = async () => {
  //     try {
  //       const items = store.cartProducts.map(item => ({
  //         ownGarment: item.own_garment,
  //         kvatt: item.kvatt,
  //         sku: item.sku,
  //         quantity: item.quantity,
  //       }));

  //       // Prefetch main cart (background mode - no calculating state)
  //       // Only if we don't already have payment intent and cart is not initialised
  //       if (!store.pd?.cs && !store.cartInitialised) {
  //         console.log('[ROOTSTATE DEBUG] Prefetching main cart on site load (background)');
  //         fetchCartState(store, updateAndSave, false, true).catch(err => {
  //           console.warn('[ROOTSTATE DEBUG] Main cart prefetch error:', err);
  //         });
  //       }

  //       // Prefetch express cart if we have shipping address
  //       if (!store.expressCartData?.expressPd?.cs && store.shipping_address?.country_code) {
  //         console.log('[ROOTSTATE DEBUG] Prefetching express cart on site load');
  //         const expressCartData = {
  //           ss: store.ss,
  //           ec: true,
  //           ca: {
  //             items: items,
  //             promo: store.promoCode?.promo_code || "",
  //             sa: {
  //               country_code: store.shipping_address.country_code,
  //               zip: store.shipping_address.zip,
  //               address1: store.shipping_address.address1 || '',
  //               address2: store.shipping_address.address2 || '',
  //               city: store.shipping_address.city,
  //               locality: store.shipping_address.locality,
  //               name: store.fullName || '',
  //               phone: store.contactNumber || '',
  //               complete: true
  //             },
  //             sp: {
  //               shippingID: store.shippingID,
  //               packagingID: store.packagingID
  //             },
  //             ...(store.emailAddress ? { cd: { email: store.emailAddress } } : {})
  //           },
  //         };

  //         API.post('api', '/checkout/cart', { body: expressCartData })
  //           .then(response => {
  //             if (response?.ss && response?.expressPd?.cs) {
  //               updateGlobal({ expressCartData: response });
  //               console.log('[ROOTSTATE DEBUG] Express cart prefetched:', {
  //                 hasExpressPd: !!response?.expressPd?.cs,
  //                 expressTotalPrice: response?.ts?.total_price
  //               });
  //             }
  //           })
  //           .catch(err => {
  //             console.warn('[ROOTSTATE DEBUG] Express cart prefetch error:', err);
  //           });
  //       }
  //     } catch (error) {
  //       console.warn('[ROOTSTATE DEBUG] Cart prefetch error:', error);
  //     }
  //   };

  //   // Delay prefetch slightly to not block initial page load
  //   setTimeout(prefetchCarts, 3000); // 3 second delay after page load
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [store.cartProducts?.length, store.ss?.oid, store.shipping_address?.country_code]); // Prefetch when we have cart data


  addReducer("fetchCart", (global, dispatch, force) => {
    if (isPaymentPage()) return
    // Use global (current state) instead of store (closure) to get latest confirmationTokenId
    fetchCartState(global, updateAndSave, force)
  })

  addReducer("setPromoCode", (global, dispatch, promocode) => {
    console.log('[PROMO DEBUG] setPromoCode reducer called with:', promocode)
    const result = setPromoCode(global, dispatch, promocode)
    console.log('[PROMO DEBUG] setPromoCode result:', result)
    updateAndSave(result).then(() => {
      // Always fetch cart to validate promo code - promo component is only in checkout
      const currentState = storeRef.current
      console.log('[PROMO DEBUG] Fetching cart after setting promo code, checkoutOpen:', currentState.checkoutOpen, 'promoCode:', currentState.promoCode?.promo_code)
      // Force fetch to validate promo code - this will update ts.pc with validation result
      fetchCartState(currentState, updateAndSave, true)
    })
  })

  addReducer("clearPromoCode", (global, dispatch) => {
    const result = clearPromoCode(global, dispatch)
    updateAndSave(result).then(() => {
      // Always fetch cart when clearing promo code to update totals
      const currentState = storeRef.current
      console.log('[PROMO DEBUG] Fetching cart after clearing promo code, checkoutOpen:', currentState.checkoutOpen)
      // Force fetch to update cart without promo code
      fetchCartState(currentState, updateAndSave, true)
    })
  })

  addReducer("differentBilling", (global, dispatch, differentBilling) => {
    updateAndSave(differentBilling(global, dispatch, differentBilling))
  })

  addReducer("setAddress", (global, dispatch, data) => {
    updateAndSave(setAddress(global, dispatch, data))
  })

  addReducer("setDiscount", (global, dispatch, discountData) => {
    const result = setDiscount(global, dispatch, discountData)
    if (result.discount && result.discount.calculated) {
      discountApplied(store, result.discount.value)
    }
    updateAndSave(result)
  })

  addReducer("setExpressShipping", (global, dispatch, shippingData) => {
    updateAndSave(setExpressShipping(global, dispatch, shippingData))
  })

  addReducer("setShipping", (global, dispatch, shippingData) => {
    updateAndSave(setShipping(global, dispatch, shippingData))
  })

  addReducer("setPackagingOption", (global, dispatch, packagingData) => {
    updateAndSave(setPackagingOption(global, dispatch, packagingData))
  })

  addReducer("clearDiscount", (global, dispatch, discountData) => {
    updateAndSave(clearDiscount(global, dispatch, discountData))
  })

  addReducer("clearShipping", (global, dispatch, shippingData) => {
    clearShipping(store)
  })

  addReducer("fetchShippingRates", (global, dispatch, addressData) => {
    // This reducer handles fetching shipping rates based on address
    updateAndSave({
      fetchingShipping: true,
      shippingError: null
    })
    // Fetch cart state which includes shipping rates
    fetchCartState(store, updateAndSave)
  })

  addReducer("changeSize", (global, dispatch, changedProduct) => {
    const result = changeSize(global, dispatch, changedProduct)
    updateCart(result.cartProducts)
  })


  addReducer("paymentComplete", async (global, dispatch, order) => {
    const result = paymentComplete(global, dispatch, order)
    if (Object.keys(result).length > 0) {
      await updateAndSave(result, global)
    }
  })

  addReducer("emptyCart", (global, dispatch) => {
    let empty = emptyCart(store)
    updateAndSave(empty)
  })

  addReducer('removeProduct', (global, dispatch, product) => {
    removeProduct(global, product)
  })

  addReducer('addToCart', (global, dispatch, product) => {
    const cartProducts = store.cartProducts || []
    console.log("STORE ADD TO CART", store, product)
    let productAlreadyInCart = false

    cartProducts.forEach((cp) => {
      if (cp.sku === product.sku) {
        cp.quantity += product.quantity
        productAlreadyInCart = true
      }
    })
    if (!productAlreadyInCart) {
      itemAdded(product)
      cartProducts.push(product)
    }

    updateCart(cartProducts)
  })

  const updateCart = (cartProducts) => {
    let productQuantity = cartProducts.reduce((sum, p) => {
      sum += p.quantity
      return sum
    }, 0)
    let totalPrice = cartProducts.reduce((sum, p) => {
      sum += p.price * p.quantity * 100
      return sum
    }, 0)

    if (productQuantity == 0) {
      const empty = emptyCart(store)
      updateAndSave(empty)
    } else {
      let cartTotal = {
        productQuantity,
        totalPrice,
        currencyId: 'GBP',
        currencyFormat: '£',
      }

      updateAndSave({
        cartProducts: cartProducts,
        cartOpen: true,
        showPayment: true,
        preparePayment: false,
        cartTotal: cartTotal,
        itemsInCart: productQuantity > 0,
        searchVisible: false,
        hitsRendered: false
      }, store)
    }
  }

  addReducer('changeProductQuantity', (global, dispatch, changedProduct) => {
    console.log('CHANGE PRODUCT QUANTITY', changedProduct)
    var cartProducts = store.cartProducts || []
    const product = cartProducts.find((p) => p.sku === changedProduct.sku)
    if (product) {
      product.quantity = changedProduct.quantity
      if (product.quantity <= 0) {
        removeProduct(store, product)
        return // removeProduct already calls updateCart
      }
    }
    updateCart(cartProducts)
  })

  const removeProduct = (global, product) => {
    var cartProducts = store.cartProducts || []
    const index = cartProducts.findIndex((p) => p.sku === product.sku)
    if (index >= 0) {
      itemRemoved(product)
      cartProducts.splice(index, 1)
      updateCart(cartProducts)
    }
  }


  const fetchCart = useDispatch("fetchCart")

  return (
    <ErrorBoundary>
      <GlobalContext.Provider value={{ store, updateGlobal }}>
        <OnlineStatusProvider>
          <Helmet lang="en">
            <script
              type="text/javascript"
              src="https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
              async
            />
            <meta charSet="utf-8" />
            <meta httpEquiv="x-ua-compatible" content="ie=edge" />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1, maximum-scale=1"
            />
          </Helmet>

          <ChakraProvider theme={theme}>
            <Logging />
            <Global styles={fonts} />
            <Header store={store} updateGlobal={updateGlobal} />
            <LeftDrawer />
            {children}
          </ChakraProvider>
        </OnlineStatusProvider>
      </GlobalContext.Provider>
    </ErrorBoundary>
  )
}

/**
 * Hook to access the global store
 * @returns {Object} Store context value
 */
export const useStore = () => {
  const store = useContext(GlobalContext)
  return store
}

export const query = graphql`
  fragment SiteData on DatoCmsSite {
    id
    globalSeo {
      siteName
      titleSuffix
      twitterAccount
      facebookPageUrl
      fallbackSeo {
        title
        description
        twitterCard
        image {
          url
          gatsbyImageData
          smartTags
        }
      }
    }
    faviconMetaTags {
      ...GatsbyDatoCmsFaviconMetaTags
    }
  }
`

// Export cartEmpty for use in other modules
export { cartEmpty }
